/**
 * 全屏控制工具
 * 支持任意 DOM 元素的两种全屏方式：视窗内全屏（fixed 铺满视窗）与浏览器原生全屏。
 */

// 浏览器全屏 API 的厂商前缀类型（与 Document 交叉，仅补充可选属性）
type FullscreenDocument = Document & {
  fullscreenEnabled?: boolean;
  fullscreenElement?: Element | null;
  exitFullscreen?: () => Promise<void>;
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  mozFullScreenEnabled?: boolean;
  mozFullScreenElement?: Element | null;
  mozCancelFullScreen?: () => Promise<void>;
  msFullscreenEnabled?: boolean;
  msFullscreenElement?: Element | null;
  msExitFullscreen?: () => Promise<void>;
};

type FullscreenElement = HTMLElement & {
  requestFullscreen?: (options?: FullscreenOptions) => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
};

const doc = document as FullscreenDocument;

// 跟踪视窗内全屏状态（单例）
let isInViewportFullscreen = false;
let viewportFullscreenElement: HTMLElement | null = null;
let originalStyles: Record<string, string> = {};

/** 全屏样式配置（用于视窗内全屏） */
export interface FullscreenStyleOptions {
  /** 是否修改背景色 */
  changeBackground?: boolean;
  /** 是否修改外边距 */
  changeMargin?: boolean;
  /** 是否修改内边距 */
  changePadding?: boolean;
  /** 是否修改边框 */
  changeBorder?: boolean;
  /** 是否修改过渡效果 */
  changeTransition?: boolean;
  /** 是否修改溢出处理方式 */
  changeOverflow?: boolean;
  /** 自定义 z-index */
  zIndex?: number;
  /** 自定义背景色 */
  backgroundColor?: string;
}

const DEFAULT_FULLSCREEN_OPTIONS: FullscreenStyleOptions = {
  changeBackground: false,
  changeMargin: true,
  changePadding: false,
  changeBorder: false,
  changeTransition: false,
  changeOverflow: true,
  zIndex: 1000,
  backgroundColor: "#fff",
};

function saveOriginalStyles(element: HTMLElement): void {
  originalStyles = {
    position: element.style.position,
    top: element.style.top,
    left: element.style.left,
    width: element.style.width,
    height: element.style.height,
    zIndex: element.style.zIndex,
    maxWidth: element.style.maxWidth,
    maxHeight: element.style.maxHeight,
    overflow: element.style.overflow,
    transition: element.style.transition,
    transform: element.style.transform,
    margin: element.style.margin,
    padding: element.style.padding,
    border: element.style.border,
    borderRadius: element.style.borderRadius,
    boxShadow: element.style.boxShadow,
    backgroundColor: element.style.backgroundColor,
  };
}

function restoreOriginalStyles(element: HTMLElement): void {
  const style = element.style as unknown as Record<string, string>;
  (Object.keys(originalStyles) as string[]).forEach((key) => {
    if (key in style && key !== "length" && key !== "parentRule") {
      style[key] = originalStyles[key];
    }
  });
  (originalStyles as Record<string, string>) = {};
}

function getBrowserFullscreenElement(): Element | null {
  return (
    doc.fullscreenElement ??
    doc.webkitFullscreenElement ??
    doc.mozFullScreenElement ??
    doc.msFullscreenElement ??
    null
  );
}

/** 是否支持浏览器原生全屏 API */
export function isFullscreenEnabled(): boolean {
  return !!(
    doc.fullscreenEnabled ??
    doc.webkitFullscreenEnabled ??
    doc.mozFullScreenEnabled ??
    doc.msFullscreenEnabled
  );
}

/** 当前是否处于浏览器全屏 */
export function isBrowserFullscreen(): boolean {
  return !!getBrowserFullscreenElement();
}

/** 当前是否处于视窗内全屏 */
export function isViewportFullscreen(): boolean {
  return isInViewportFullscreen;
}

/** 指定元素是否处于任意一种全屏状态 */
export function isElementFullscreen(element: HTMLElement): boolean {
  if (!element) return false;
  const fsEl = getBrowserFullscreenElement();
  return (
    fsEl === element ||
    (isInViewportFullscreen && viewportFullscreenElement === element)
  );
}

/** 进入浏览器原生全屏（默认对 document.documentElement） */
export async function enterBrowserFullscreen(
  element: HTMLElement = document.documentElement
): Promise<void> {
  if (!element) throw new Error("未提供有效的 DOM 元素");
  if (isBrowserFullscreen()) return;

  const el = element as FullscreenElement;
  if (el.requestFullscreen) {
    await el.requestFullscreen();
  } else if (el.webkitRequestFullscreen) {
    await el.webkitRequestFullscreen();
  } else if (el.mozRequestFullScreen) {
    await el.mozRequestFullScreen();
  } else if (el.msRequestFullscreen) {
    await el.msRequestFullscreen();
  } else {
    throw new Error("当前浏览器不支持全屏 API");
  }
  viewportFullscreenElement = null;
}

/** 退出浏览器全屏 */
export async function exitBrowserFullscreen(): Promise<void> {
  if (!isBrowserFullscreen()) return;
  if (doc.exitFullscreen) {
    await doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    await doc.webkitExitFullscreen();
  } else if (doc.mozCancelFullScreen) {
    await doc.mozCancelFullScreen();
  } else if (doc.msExitFullscreen) {
    await doc.msExitFullscreen();
  }
  viewportFullscreenElement = null;
  isInViewportFullscreen = false;
}

/** 进入视窗内全屏（fixed 铺满视窗，不触发浏览器全屏） */
export function enterViewportFullscreen(
  element: HTMLElement,
  container: HTMLElement = document.body,
  options?: FullscreenStyleOptions
): void {
  if (!element) throw new Error("未提供有效的 DOM 元素");
  if (isInViewportFullscreen && viewportFullscreenElement === element) return;

  const styleOptions = { ...DEFAULT_FULLSCREEN_OPTIONS, ...options };
  viewportFullscreenElement = element;
  saveOriginalStyles(element);

  element.style.position = "fixed";
  element.style.top = "0";
  element.style.left = "0";
  element.style.width = "100%";
  element.style.height = "100%";
  element.style.zIndex = String(styleOptions.zIndex ?? 9999);
  element.style.maxWidth = "100%";
  element.style.maxHeight = "100%";

  if (styleOptions.changeMargin) element.style.margin = "0";
  if (styleOptions.changePadding) {
    element.style.padding =
      container === document.body ? "0" : (originalStyles.padding ?? "");
  }
  if (styleOptions.changeOverflow) element.style.overflow = "auto";
  if (styleOptions.changeTransition)
    element.style.transition = "all 0.3s ease";
  if (styleOptions.changeBackground && styleOptions.backgroundColor) {
    element.style.backgroundColor = styleOptions.backgroundColor;
  }

  element.setAttribute("data-fullscreen", "viewport");
  isInViewportFullscreen = true;
  window.dispatchEvent(new Event("resize"));
}

/** 退出视窗内全屏 */
export function exitViewportFullscreen(): void {
  if (!isInViewportFullscreen || !viewportFullscreenElement) return;
  restoreOriginalStyles(viewportFullscreenElement);
  viewportFullscreenElement.removeAttribute("data-fullscreen");
  viewportFullscreenElement = null;
  isInViewportFullscreen = false;
  window.dispatchEvent(new Event("resize"));
}

/** 切换浏览器全屏 */
export async function toggleBrowserFullscreen(
  element: HTMLElement
): Promise<void> {
  if (isBrowserFullscreen() && getBrowserFullscreenElement() === element) {
    await exitBrowserFullscreen();
  } else {
    await enterBrowserFullscreen(element);
  }
}

/** 切换视窗内全屏 */
export function toggleViewportFullscreen(
  element: HTMLElement,
  container: HTMLElement = document.body,
  options?: FullscreenStyleOptions
): void {
  if (isViewportFullscreen() && viewportFullscreenElement === element) {
    exitViewportFullscreen();
  } else {
    enterViewportFullscreen(element, container, options);
  }
}

/** 监听浏览器全屏状态变化，返回取消订阅函数 */
export function onFullscreenChange(
  callback: (isFullscreen: boolean) => void
): () => void {
  const handler = () => callback(isBrowserFullscreen());
  document.addEventListener("fullscreenchange", handler);
  document.addEventListener("webkitfullscreenchange", handler);
  document.addEventListener("mozfullscreenchange", handler);
  document.addEventListener("MSFullscreenChange", handler);
  return () => {
    document.removeEventListener("fullscreenchange", handler);
    document.removeEventListener("webkitfullscreenchange", handler);
    document.removeEventListener("mozfullscreenchange", handler);
    document.removeEventListener("MSFullscreenChange", handler);
  };
}

/** 为按钮绑定全屏切换（点击按钮切换目标元素全屏），返回解绑函数 */
export function createFullscreenToggle(
  targetElement: HTMLElement,
  buttonElement: HTMLElement,
  options: {
    useViewportFullscreen?: boolean;
    container?: HTMLElement;
    styleOptions?: FullscreenStyleOptions;
    onEnterFullscreen?: () => void;
    onExitFullscreen?: () => void;
  } = {}
): () => void {
  const {
    useViewportFullscreen = false,
    container = document.body,
    styleOptions,
    onEnterFullscreen,
    onExitFullscreen,
  } = options;
  const useViewport = useViewportFullscreen || !isFullscreenEnabled();

  const handleClick = async () => {
    try {
      if (useViewport) {
        const isFs =
          isViewportFullscreen() && viewportFullscreenElement === targetElement;
        if (isFs) {
          exitViewportFullscreen();
          onExitFullscreen?.();
        } else {
          enterViewportFullscreen(targetElement, container, styleOptions);
          onEnterFullscreen?.();
        }
      } else {
        const fsEl = getBrowserFullscreenElement();
        const isFs = fsEl === targetElement;
        if (isFs) {
          await exitBrowserFullscreen();
          onExitFullscreen?.();
        } else {
          await enterBrowserFullscreen(targetElement);
          onEnterFullscreen?.();
        }
      }
    } catch (err) {
      console.error("切换全屏状态失败", err);
    }
  };

  buttonElement.addEventListener("click", handleClick);
  return () => buttonElement.removeEventListener("click", handleClick);
}

/** 全屏工具聚合对象（兼容旧用法） */
export const fullscreen = {
  isEnabled: isFullscreenEnabled,
  isBrowserFullscreen,
  isViewportFullscreen,
  isElementFullscreen,
  enterBrowser: enterBrowserFullscreen,
  exitBrowser: exitBrowserFullscreen,
  enterViewport: enterViewportFullscreen,
  exitViewport: exitViewportFullscreen,
  toggleBrowser: toggleBrowserFullscreen,
  toggleViewport: toggleViewportFullscreen,
  onChange: onFullscreenChange,
  createToggle: createFullscreenToggle,
};
